import ResourceTyped from 'Resources/ResourceTyped';

// Routines (cron / event-hook / webhook triggers) resource, split from the
// Assistant god resource (WEB-7958). Same URLs — pure client-side reorg.

export type RoutineScope = 'user' | 'org';

export type HookFilter = {
	field: string;
	operator: string;
	value?: string | number | boolean | null;
};

export type Routine = {
	id: number;
	userId: number;
	scope: RoutineScope;
	agentId?: number | null;
	/** A routine bound to a workflow runs it, not an agent, so it needs no org agent (WEB-15479). */
	workflowId?: number | null;
	label?: string;
	prompt?: string;
	enabled: boolean;
	conversationId?: number;
	cronExpression?: string | null;
	lastRun?: string;
	nextRun?: string;
	entityType?: string | null;
	event?: 'insert' | 'update' | null;
	filters?: HookFilter[];
	cooldownSeconds?: number;
	lastFired?: string;
	/** Which trigger kind fires the routine. Derived: webhookHash ⇒ 'webhook', entityType ⇒ 'event', cronExpression ⇒ 'cron'. */
	trigger?: 'cron' | 'event' | 'webhook';
	/** Set for webhook-scoped routines (WEB-7446). The unguessable `wh_` hash that forms the public endpoint URL. */
	webhookHash?: string | null;
	/** Webhook tuning — currently just the per-routine rate-limit override (req/min, capped at 1000). */
	webhookConfig?: { rateLimit?: number } | null;
	regDate?: string;
	modDate?: string;
};

/** A single inbound webhook delivery (one dispatched run), for the detail panel (WEB-7446). */
export type WebhookDelivery = {
	id: number;
	status: string;
	error?: string | null;
	startedAt?: string | null;
	finishedAt?: string | null;
};

class AssistantRoutines extends ResourceTyped<Routine> {
	constructor() {
		super('assistant/routines');
	}

	/** `all`: every user's routines - admins only, behind AI_AUTOMATION_CONTROLS (WEB-15491). */
	getRoutines({ all = false }: { all?: boolean } = {}): Promise<Routine[]> {
		return fetch(`/api/v2/assistant/routines${all ? '?all=1' : ''}`)
			.then(res => res.json())
			.then(res => res.data);
	}

	createRoutine(routine: {
		scope?: RoutineScope;
		label?: string;
		prompt?: string;
		cronExpression?: string;
		entityType?: string;
		event?: 'insert' | 'update';
		filters?: HookFilter[];
		cooldownSeconds?: number;
		agentId?: number | null;
		/** Pass 'webhook' to mint a webhook-scoped routine (server generates + registers the hash). */
		trigger?: 'webhook';
		webhookConfig?: { rateLimit?: number } | null;
	}): Promise<Routine> {
		return fetch('/api/v2/assistant/routines', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(routine)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	async updateRoutine(
		routineId: number,
		updates: {
			scope?: RoutineScope;
			label?: string;
			prompt?: string;
			cronExpression?: string | null;
			entityType?: string | null;
			event?: 'insert' | 'update' | null;
			filters?: HookFilter[] | null;
			cooldownSeconds?: number;
			enabled?: boolean;
			agentId?: number | null;
			webhookConfig?: { rateLimit?: number } | null;
		}
	): Promise<void> {
		const res = await fetch(`/api/v2/assistant/routines/${routineId}`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		});
		if (!res.ok) {
			const body = await res.json().catch(() => null);
			throw new Error(body?.error?.msg || `Failed to update routine (${res.status})`);
		}
	}

	deleteRoutine(routineId: number): Promise<void> {
		return fetch(`/api/v2/assistant/routines/${routineId}`, { method: 'DELETE' }).then(() => undefined);
	}

	/**
	 * WEB-7446 — rotate a webhook routine's hash. The old URL stops resolving
	 * immediately (receiver 404s unknown hashes). Returns the new hash so the
	 * caller can re-render the endpoint URL without a refetch.
	 */
	async rotateWebhookHash(routineId: number): Promise<{ webhookHash: string }> {
		const res = await fetch(`/api/v2/assistant/routines/${routineId}/rotate-webhook`, { method: 'POST' });
		const body = await res.json().catch(() => null);
		if (!res.ok) {
			throw new Error(body?.error?.msg || `Failed to rotate webhook (${res.status})`);
		}
		return body.data as { webhookHash: string };
	}

	/** WEB-7446 — recent inbound deliveries (dispatched runs) for a webhook routine. */
	getRoutineDeliveries(routineId: number): Promise<WebhookDelivery[]> {
		return fetch(`/api/v2/assistant/routines/${routineId}/deliveries`)
			.then(res => res.json())
			.then(res => res.data ?? []);
	}
}

const resource = new AssistantRoutines();

export default resource;
